import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle } from "lucide-react";
import { Button, Card, EmptyState, ErrorText, FormSection, Input, Select, Skeleton, useToast } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";
import { ApiError, apiFetch } from "../../lib/api";

interface InvoiceSettings {
  invoicePrefix: string;
  numberPadding: number;
  footerTextAr: string | null;
  footerTextEn: string | null;
  defaultFormat: "a4_pdf" | "thermal_80" | "thermal_58";
  thermalWidthMm: 58 | 80;
  showQr: boolean;
  showLogo: boolean;
  receiptLanguage: "ar" | "en" | "both";
  defaultCopies: number;
  autoPrintDirectSale: boolean;
  printPreview: boolean;
  logoSize: "small" | "medium" | "large";
  logoAlign: "start" | "center" | "end";
  showBusinessName: boolean;
  showVatNumber: boolean;
  showAddress: boolean;
  showDatetime: boolean;
  showCustomer: boolean;
  showPaymentMethod: boolean;
  showTaxDetail: boolean;
  showNotes: boolean;
  qrSize: "small" | "medium" | "large";
  qrPosition: "top" | "bottom";
}

/** A labelled checkbox row — used enough here to be worth naming. */
function Toggle({
  label, hint, checked, onChange, disabled,
}: {
  label: string; hint?: string; checked: boolean;
  onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 shrink-0 rounded border-line-strong text-accent-500"
      />
      <span className="min-w-0">
        <span className="block text-sm text-ink-900">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </label>
  );
}

/**
 * Invoice presentation — what the printed document shows.
 *
 * EVERY control here is presentation. None can change a figure: an issued
 * invoice's money lives in snapshot columns frozen at issuance, so a reprint
 * after a change here shows the same totals it always did. That is enforced in
 * the schema (migration 0011) and by the renderers reading the snapshot, not
 * by this screen being careful.
 */
export function PrintSettingsSection() {
  const { hasPermission } = useAuth();
  const { language } = useBranding();
  const { showToast } = useToast();
  const ar = language === "ar";

  const [form, setForm] = useState<InvoiceSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const canEdit = hasPermission("tax_settings.update");

  const load = async () => {
    setLoadError(null);
    try {
      setForm(await apiFetch<InvoiceSettings>("/settings/invoice"));
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : ar ? "تعذّر التحميل." : "Couldn't load.");
    }
  };

  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof InvoiceSettings>(key: K, value: InvoiceSettings[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaveError(null);
    setSaving(true);
    try {
      await apiFetch("/settings/invoice", { method: "PUT", body: form });
      showToast("success", ar ? "حُفظت إعدادات الطباعة." : "Print settings saved.");
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : ar ? "تعذّر الحفظ." : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Card>
        <EmptyState icon={<AlertTriangle className="size-6" />} title={ar ? "تعذّر التحميل" : "Couldn't load"}
          description={loadError} action={<Button size="sm" onClick={load}>{ar ? "إعادة المحاولة" : "Retry"}</Button>} />
      </Card>
    );
  }
  if (!form) return <Card><Skeleton className="h-96 w-full" /></Card>;

  return (
    <form onSubmit={save} className="space-y-4">
      <Card>
        <FormSection legend={ar ? "الطباعة" : "Printing"}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Select label={ar ? "الصيغة الافتراضية" : "Default format"} value={form.defaultFormat}
              onChange={(e) => set("defaultFormat", e.target.value as InvoiceSettings["defaultFormat"])} disabled={!canEdit}>
              <option value="a4_pdf">A4</option>
              <option value="thermal_80">{ar ? "حراري ٨٠مم" : "Thermal 80mm"}</option>
              <option value="thermal_58">{ar ? "حراري ٥٨مم" : "Thermal 58mm"}</option>
            </Select>
            <Input label={ar ? "عدد النسخ المعتاد" : "Usual copies"} type="number" min={1} max={5} value={form.defaultCopies}
              onChange={(e) => set("defaultCopies", Number(e.target.value) || 1)} disabled={!canEdit}
              // Stated plainly rather than implied. A browser gives a page no
              // way to set the print dialog's copy count, so pretending this
              // controls the printer would be a lie the operator discovers at
              // the counter. It is a recorded preference and a reminder.
              helperText={ar
                ? "تذكير للمشغّل — يضبط المتصفح عدد النسخ فعلياً من نافذة الطباعة."
                : "An operator reminder — the browser's own print dialog sets the actual copy count."} />
            {/* No "both" option. No template — browser or PDF — renders two
                languages side by side, and a 58mm roll could not carry them
                anyway. Offering it would let an owner choose a setting that
                silently resolves to Arabic. The value stays accepted on the
                API for rows that already hold it; it just isn't offered. */}
            <Select label={ar ? "لغة الإيصال" : "Receipt language"} value={form.receiptLanguage === "both" ? "ar" : form.receiptLanguage}
              onChange={(e) => set("receiptLanguage", e.target.value as InvoiceSettings["receiptLanguage"])} disabled={!canEdit}
              helperText={ar
                ? "لغة المستند المطبوع — مستقلة عن لغة الواجهة، وتُطبَّق على الطباعة و PDF معاً."
                : "The printed document's language — independent of the interface, applied to both print and PDF."}>
              <option value="ar">{ar ? "العربية" : "Arabic"}</option>
              <option value="en">{ar ? "الإنجليزية" : "English"}</option>
            </Select>
          </div>
          <div className="mt-3 space-y-1">
            <Toggle label={ar ? "طباعة تلقائية بعد البيع المباشر" : "Auto-print after Direct Sale"}
              hint={ar ? "تُفتح الطباعة فور اكتمال البيع فعلياً — لا قبل ذلك." : "Fires only after the sale is actually committed."}
              checked={form.autoPrintDirectSale} onChange={(v) => set("autoPrintDirectSale", v)} disabled={!canEdit} />
            <Toggle label={ar ? "معاينة قبل الطباعة" : "Print preview"}
              checked={form.printPreview} onChange={(v) => set("printPreview", v)} disabled={!canEdit} />
          </div>
        </FormSection>
      </Card>

      <Card>
        <FormSection legend={ar ? "الشعار" : "Logo"}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Select label={ar ? "الحجم" : "Size"} value={form.logoSize}
              onChange={(e) => set("logoSize", e.target.value as InvoiceSettings["logoSize"])} disabled={!canEdit || !form.showLogo}>
              <option value="small">{ar ? "صغير" : "Small"}</option>
              <option value="medium">{ar ? "متوسط" : "Medium"}</option>
              <option value="large">{ar ? "كبير" : "Large"}</option>
            </Select>
            <Select label={ar ? "المحاذاة" : "Alignment"} value={form.logoAlign}
              onChange={(e) => set("logoAlign", e.target.value as InvoiceSettings["logoAlign"])} disabled={!canEdit || !form.showLogo}>
              <option value="start">{ar ? "البداية" : "Start"}</option>
              <option value="center">{ar ? "الوسط" : "Center"}</option>
              <option value="end">{ar ? "النهاية" : "End"}</option>
            </Select>
          </div>
          <div className="mt-3">
            <Toggle label={ar ? "إظهار الشعار" : "Show logo"} checked={form.showLogo}
              onChange={(v) => set("showLogo", v)} disabled={!canEdit} />
          </div>
        </FormSection>
      </Card>

      <Card>
        <FormSection legend={ar ? "محتوى الفاتورة" : "Invoice content"}>
          <div className="grid gap-x-6 sm:grid-cols-2">
            <Toggle label={ar ? "اسم المنشأة" : "Business name"} checked={form.showBusinessName} onChange={(v) => set("showBusinessName", v)} disabled={!canEdit} />
            <Toggle label={ar ? "الرقم الضريبي" : "VAT number"} checked={form.showVatNumber} onChange={(v) => set("showVatNumber", v)} disabled={!canEdit} />
            <Toggle label={ar ? "العنوان" : "Address"} checked={form.showAddress} onChange={(v) => set("showAddress", v)} disabled={!canEdit} />
            <Toggle label={ar ? "التاريخ والوقت" : "Date and time"} checked={form.showDatetime} onChange={(v) => set("showDatetime", v)} disabled={!canEdit} />
            <Toggle label={ar ? "بيانات العميل" : "Customer details"} checked={form.showCustomer} onChange={(v) => set("showCustomer", v)} disabled={!canEdit} />
            <Toggle label={ar ? "طريقة الدفع" : "Payment method"} checked={form.showPaymentMethod} onChange={(v) => set("showPaymentMethod", v)} disabled={!canEdit} />
            <Toggle label={ar ? "تفاصيل الضريبة" : "Tax detail"} checked={form.showTaxDetail} onChange={(v) => set("showTaxDetail", v)} disabled={!canEdit} />
            <Toggle label={ar ? "ملاحظات الفاتورة" : "Invoice notes"} checked={form.showNotes} onChange={(v) => set("showNotes", v)} disabled={!canEdit} />
          </div>
        </FormSection>
      </Card>

      <Card>
        <FormSection legend={ar ? "رمز QR" : "QR code"}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Select label={ar ? "الحجم" : "Size"} value={form.qrSize}
              onChange={(e) => set("qrSize", e.target.value as InvoiceSettings["qrSize"])} disabled={!canEdit || !form.showQr}>
              <option value="small">{ar ? "صغير" : "Small"}</option>
              <option value="medium">{ar ? "متوسط" : "Medium"}</option>
              <option value="large">{ar ? "كبير" : "Large"}</option>
            </Select>
            <Select label={ar ? "الموضع" : "Position"} value={form.qrPosition}
              onChange={(e) => set("qrPosition", e.target.value as InvoiceSettings["qrPosition"])} disabled={!canEdit || !form.showQr}>
              <option value="top">{ar ? "أعلى" : "Top"}</option>
              <option value="bottom">{ar ? "أسفل" : "Bottom"}</option>
            </Select>
          </div>
          <div className="mt-3">
            <Toggle label={ar ? "إظهار رمز QR" : "Show QR code"}
              // Said plainly rather than hidden: ZATCA Phase-1 expects a
              // scannable QR on a simplified tax invoice, so switching it off
              // is a compliance decision the owner should make knowingly.
              hint={ar
                ? "تتطلب المرحلة الأولى من هيئة الزكاة والضريبة رمز QR على الفاتورة المبسطة."
                : "ZATCA Phase-1 expects a QR on a simplified tax invoice."}
              checked={form.showQr} onChange={(v) => set("showQr", v)} disabled={!canEdit} />
          </div>
        </FormSection>
      </Card>

      <Card>
        <FormSection legend={ar ? "التذييل والترقيم" : "Footer and numbering"}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label={ar ? "التذييل (عربي)" : "Footer (Arabic)"} value={form.footerTextAr ?? ""}
              onChange={(e) => set("footerTextAr", e.target.value)} disabled={!canEdit} />
            <Input label={ar ? "التذييل (إنجليزي)" : "Footer (English)"} value={form.footerTextEn ?? ""}
              onChange={(e) => set("footerTextEn", e.target.value)} disabled={!canEdit} />
            <Input label={ar ? "بادئة رقم الفاتورة" : "Invoice prefix"} value={form.invoicePrefix}
              onChange={(e) => set("invoicePrefix", e.target.value.toUpperCase())} disabled={!canEdit}
              helperText={ar ? "أحرف إنجليزية كبيرة وأرقام وشرطة." : "Uppercase letters, digits and hyphen."} />
            <Input label={ar ? "عدد خانات الترقيم" : "Number padding"} type="number" min={1} max={12}
              value={form.numberPadding} onChange={(e) => set("numberPadding", Number(e.target.value) || 6)} disabled={!canEdit} />
          </div>
        </FormSection>
      </Card>

      <ErrorText>{saveError}</ErrorText>

      {canEdit && (
        <div className="flex justify-end">
          <Button type="submit" loading={saving}>{ar ? "حفظ" : "Save"}</Button>
        </div>
      )}
    </form>
  );
}
